import "./sentry";
import "./i18n"; // Initialize i18next with the bundled locale files
import * as Sentry from "@sentry/react";
import React, { useEffect, useState, lazy, Suspense } from "react";
import { useTranslation } from "react-i18next";
import { Toaster } from "@/components/ui/sonner";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { IntercomProvider } from "react-use-intercom";
import BookingCalendar from "./components/BookingCalendar";
import BookingSkeleton from "./components/BookingSkeleton";
import SessionLoadingSpinner from "./components/booking/SessionLoadingSpinner";
import EmailCaptureStep from "./components/EmailCaptureStep";
import CookieConsentBanner from "./components/CookieConsent";

const SESSION_COOKIE_NAME = "booking_session_id";

// Check if there's a session cookie (means we're restoring state)
const hasSessionCookie = (): boolean => {
  if (typeof document === "undefined") return false;
  return document.cookie.includes(SESSION_COOKIE_NAME);
};
import ErrorFallback from "./components/ErrorFallback";
import NotFound from "./pages/NotFound";

import {
  getUrlParam,
  isTestModeActive,
  getStepFromUrl,
} from "@/utils/urlUtils";

// Lazy load Analysis page (admin-only, not needed in main bundle)
const Analysis = lazy(() => import("./pages/Analysis"));
import { useSentry } from "@/sentry.ts";
import { trackEvent } from "@/utils/analytics";
import { safeLocalStorage } from "@/utils/safeStorage";
import { useLanguage } from "@/hooks/booking/useLanguage.ts";
import { useDocumentMeta } from "@/hooks/booking/useDocumentMeta";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import { useProducts } from "@/components/product-step/useProducts";
import { useMarket } from "@/hooks/useMarket";
import { useBookingFlowStore } from "@/stores/bookingFlowStore";
import { supabase } from "@/integrations/supabase/client";
import {
  MembershipProvider,
  useMembership,
} from "@/contexts/MembershipContext";

// Configure the query client with basic caching defaults
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 15, // 15 minutes
      gcTime: 1000 * 60 * 60, // 60 minutes
      // No global retry setting - let each query define its own
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
    },
  },
});

// Dynamic ClickToComponent loader for development
const ClickToComponentWrapper = () => {
  const [ClickToComponent, setClickToComponent] = useState<React.ComponentType<{
    editor: string;
  }> | null>(null);

  useEffect(() => {
    // Only load in development and after the app has initialized
    if (process.env.NODE_ENV === "development") {
      const loadClickToComponent = async () => {
        try {
          const { ClickToComponent: Component } = await import(
            "click-to-react-component"
          );
          setClickToComponent(() => Component);
        } catch (error) {
          console.warn("Failed to load click-to-react-component:", error);
        }
      };

      // Add a small delay to ensure the app loads first
      const timer = setTimeout(loadClickToComponent, 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  if (!ClickToComponent) return null;

  return <ClickToComponent editor="cursor" />;
};

// Return from a Stripe REDIRECT payment (iDEAL/TWINT/Klarna/PayPal). Read at
// module load: loadSession strips `payment_return` from the URL soon after.
const IS_PAYMENT_RETURN =
  new URLSearchParams(window.location.search).get("payment_return") === "1";

// Inner component that uses hooks requiring QueryClient
const AppContent = () => {
  const [isNotFound, setIsNotFound] = useState(false);
  const [showEmailCapture, setShowEmailCapture] = useState<boolean | null>(
    null,
  ); // null = not determined yet
  const [isSpinnerFadingOut, setIsSpinnerFadingOut] = useState(false);
  const [showSpinner, setShowSpinner] = useState(hasSessionCookie());

  const { language: userLanguage } = useLanguage();
  // Sync <html lang> + document <title>/<meta description> with the active language
  // (driven off i18next, so it reacts to in-app language switches too)
  useDocumentMeta();
  const { ready: isTranslationsReady } = useTranslation();
  const { data: featureFlags, isLoading: isFlagsLoading } = useFeatureFlags();

  // Check if booking flow is loading (session restoration)
  const isSessionLoading = useBookingFlowStore((state) => state.isLoading);

  // Check if membership data is still loading
  const { isLoading: isMembershipLoading, isMember } = useMembership();

  const { market } = useMarket();

  // Warm products for every market × its languages up front, so switching
  // country never shows a skeleton flash — the products query key is
  // market-scoped, so each combo is a distinct cache entry. (Clinics and
  // availability already share one cache across markets, so only products need
  // this.) All queries fire in parallel on mount.
  const chEn = useProducts("en-US", "ch");
  const chDe = useProducts("de-DE", "ch");
  const deEn = useProducts("en-US", "de");
  const deDe = useProducts("de-DE", "de");
  const nlEn = useProducts("en-US", "nl");
  const nlNl = useProducts("nl-NL", "nl");
  const atEn = useProducts("en-US", "at");
  const atDe = useProducts("de-DE", "at");

  // Gate the loading screen on the CURRENT market only, so first paint isn't
  // slowed by the other market — it warms in the background for an instant
  // switch. Products are loaded once both languages of the active market are in.
  const activeMarketProducts =
    market === "de"
      ? [deEn, deDe]
      : market === "nl"
        ? [nlEn, nlNl]
        : market === "at"
          ? [atEn, atDe]
          : [chEn, chDe];
  const isProductsLoading = activeMarketProducts.some((q) => q.isLoading);

  // Consider products failed only if BOTH languages of the active market failed.
  const isProductsError = activeMarketProducts.every((q) => q.isError);

  // Determine if we're still in the loading phase
  const isLoading =
    showEmailCapture === null ||
    isFlagsLoading ||
    isProductsLoading ||
    isSessionLoading ||
    isMembershipLoading ||
    !isTranslationsReady;

  // Handle spinner fade-out when loading completes
  useEffect(() => {
    if (!isLoading && showSpinner && !isSpinnerFadingOut) {
      // Start fade-out
      setIsSpinnerFadingOut(true);
      // Remove spinner after fade-out completes (500ms)
      const timeout = setTimeout(() => {
        setShowSpinner(false);
        setIsSpinnerFadingOut(false);
      }, 500);
      return () => clearTimeout(timeout);
    }
  }, [isLoading, showSpinner, isSpinnerFadingOut]);

  // Update showEmailCapture when feature flags are loaded
  useEffect(() => {
    if (!isFlagsLoading && featureFlags !== undefined) {
      // Bypass email capture when test mode params are present
      if (isTestModeActive()) {
        console.log("[App] Test mode active, bypassing email capture");
        setShowEmailCapture(false);
        return;
      }

      // A paying customer coming back from the redirect must land on the
      // confirmation, not the email gate (shown when they never passed it).
      if (IS_PAYMENT_RETURN) {
        setShowEmailCapture(false);
        return;
      }

      // Bypass email capture when valid membership data is loaded
      if (isMember) {
        console.log("[App] Valid membership detected, bypassing email capture");
        setShowEmailCapture(false);
        return;
      }

      const isEmailCaptureEnabled = featureFlags["email_capture"] ?? false; // Default to false if flag not found
      const hasUserInteracted =
        safeLocalStorage.getItem("userEmail") ||
        safeLocalStorage.getItem("emailCaptureSkipped");

      // Only show email capture if:
      // 1. Feature flag is enabled
      // 2. User hasn't already provided email or skipped
      setShowEmailCapture(isEmailCaptureEnabled && !hasUserInteracted);
    }
  }, [featureFlags, isFlagsLoading, isMember]);

  // Check if the URL path suggests a 404 situation
  useEffect(() => {
    const isValidPath =
      window.location.pathname === "/" ||
      window.location.pathname === "/index.html" ||
      window.location.pathname === "/analysis";

    setIsNotFound(!isValidPath);
  }, []);

  const handleEmailSubmit = async (email: string) => {
    console.log("Email submitted:", email);

    try {
      // Call Supabase function to register email
      const { data, error } = await supabase.functions.invoke(
        "register-email",
        {
          body: {
            email,
            source: "email_capture_step",
          },
        },
      );

      if (error) {
        console.error("Failed to register email:", error);
        // Still proceed with the flow even if registration fails
      } else {
        console.log("Email registered successfully:", data);
      }

      // Track analytics event using existing utils
      trackEvent({
        event: "email_captured",
        props: {
          email: email,
          source: "email_capture_step",
          registration_success: !error,
        },
      });

      // Store email and hide capture
      safeLocalStorage.setItem("userEmail", email);
      setShowEmailCapture(false);
    } catch (error) {
      console.error("Error during email submission:", error);

      // Track failed registration in analytics
      trackEvent({
        event: "email_captured",
        props: {
          email: email,
          source: "email_capture_step",
          registration_success: false,
          error: "network_error",
        },
      });

      // Still proceed with the flow
      safeLocalStorage.setItem("userEmail", email);
      setShowEmailCapture(false);
    }
  };

  const handleEmailSkip = () => {
    console.log("Email capture skipped");

    // Track analytics event using existing utils
    trackEvent({
      event: "email_capture_skipped",
      props: {
        source: "email_capture_step",
      },
    });

    // Mark as skipped and hide capture
    safeLocalStorage.setItem("emailCaptureSkipped", "true");
    setShowEmailCapture(false);
  };

  // Check for /analysis route
  const isAnalysisRoute = window.location.pathname === "/analysis";

  if (isAnalysisRoute) {
    return (
      <Suspense
        fallback={
          <div className="min-h-screen bg-gray-50 flex items-center justify-center">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading...</p>
            </div>
          </div>
        }
      >
        <Analysis />
      </Suspense>
    );
  }

  return (
    <div className="app-container">
      <Toaster />
      <ClickToComponentWrapper />

      {/* Spinner overlay with fade-out */}
      {showSpinner && (
        <div className="absolute inset-0 z-50 pointer-events-none">
          <SessionLoadingSpinner isVisible={!isSpinnerFadingOut} />
        </div>
      )}

      {isNotFound ? (
        <NotFound />
      ) : isProductsError ? (
        // Show error fallback if products failed to load
        <ErrorFallback language={userLanguage} />
      ) : isLoading && !showSpinner ? (
        // Show skeleton for fresh loads (no session cookie)
        <BookingSkeleton
          language={userLanguage}
          step={getStepFromUrl() || "product"}
        />
      ) : showEmailCapture ? (
        <EmailCaptureStep
          onEmailSubmit={handleEmailSubmit}
          onSkip={handleEmailSkip}
          language={userLanguage}
        />
      ) : (
        <BookingCalendar />
      )}
    </div>
  );
};

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <IntercomProvider
        appId="ygu3tmdd"
        apiBase="https://api-iam.intercom.io"
        autoBoot={false}
        autoBootProps={{
          hideDefaultLauncher: true,
        }}
      >
        <MembershipProvider>
          <AppContent />
          <CookieConsentBanner />
        </MembershipProvider>
      </IntercomProvider>
    </QueryClientProvider>
  );
};

const SentryApp = () => {
  if (!useSentry) {
    return <App />;
  }

  return (
    <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
      <App />
    </Sentry.ErrorBoundary>
  );
};

export default SentryApp;
